import type { Ref } from 'vue'
import type { ApiNotifications, ApiNotificationsData } from '~/store-v2/notifications/useNotifications'
import useMarkNotificationClicked from '~/store-v2/notifications/useMarkNotificationClicked'
import useMarkNotificationsRead from '~/store-v2/notifications/useMarkNotificationsRead'
import useNotifications from '~/store-v2/notifications/useNotifications'

interface HeaderNotification {
  id: number
  clickedAt: Date | string | null
  readAt: Date | string | null
  createdAt: Date | string
  notification: {
    actionUrl: string
    message: string
    templateName: string
    title: string
  }
}

interface HeaderNotificationsList {
  count: number
  countNotification: number
  totalCount: number
  nextCursor: string
  data: HeaderNotification[]
}

export default function useNotificationsManager(userId: Ref<number>) {
  const cursor = ref<string | undefined>(undefined)
  const notificationsList = ref<ApiNotifications>()
  const countNotification = ref<number>(0)
  const countNotificationList = ref<number>(0)

  const { data: notificationsData, refetch: refetchNotifications, isError: isNotificationsError } = useNotifications(userId, cursor)
  const { mutateAsync: markAllRead } = useMarkNotificationsRead()
  const { mutateAsync: markClicked } = useMarkNotificationClicked()

  const notifications = computed<HeaderNotificationsList>(() => {
    const data = (notificationsList.value?.data ?? []) as ApiNotificationsData

    return {
      count: countNotificationList.value,
      countNotification: countNotification.value,
      totalCount: notificationsData.value?.metrics?.totalCount ?? 0,
      nextCursor: notificationsData.value?.nextCursor ?? '',
      data: data.map(n => ({
        ...n,
        clickedAt: n.clickedAt ?? null,
        readAt: n.readAt ?? null,
      })),
    }
  })

  const handleClickNotification = async (event: any) => {
    try {
      const { event: eventType, notification } = event

      if (eventType === 'toggle') {
        await markAllRead()
        countNotification.value = 0
      }

      if (eventType === 'goToNotification' && notification && userId.value) {
        await markClicked(notification.id)
      }

      if (eventType === 'more' && userId.value && notifications.value.nextCursor) {
        cursor.value = notifications.value.nextCursor
        await refetchNotifications()
      }
    }
    catch {
      console.error('Erro ao processar notificação')
    }
  }

  watch(() => notificationsData.value, (newData) => {
    if (!newData)
      return

    notificationsList.value = {
      ...newData,
      data: [...(notificationsList.value?.data ?? []), ...newData.data],
    }

    countNotification.value = newData.metrics.totalUnreadCount
    countNotificationList.value += newData.count
  }, { immediate: true })

  return {
    notifications,
    handleClickNotification,
    countNotification,
    countNotificationList,
    notificationsError: isNotificationsError,
  }
}
